"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import logjs from "@metrojs/logjs";

import { CAR_SEARCH_LIST_COLUMNS } from "./carSearchFormBridge.js";
import { resolveSyaryoPcarSearchUrl } from "./syaryoPcarConfig.js";

const log = new logjs("useSyaryoDialog");

/**
 * p_car で実用上効く検索条件のみ（車名・年式は API 絞り込みに効かないため除外）
 * @see file/EPS_Parts/OldDLLMode.cs GetCarParamVer2
 */
export const CRITERIA_FIELDS = [
  { key: "carModelNumber", label: "型式指定", width: 120 },
  { key: "carTypeNumber", label: "類別区分", width: 120 },
  { key: "carSharyoKatashiki", label: "車輌型式", width: 180 },
  { key: "carNumber", label: "車台番号", width: 220 },
];

/** 一覧・選択後に親へ渡す行（モック・CarSearch 想定） */
export const MOCK_ROWS = [
  {
    carMakerName: "トヨタ",
    carMakerCode: "1",
    carName: "ヴォクシー",
    carModel: "DBA-ZRR80G",
    carSharyoKatashiki: "DBA-ZRR80G",
    carDisplacementNm: "2000",
    carModelNumber: "18251",
    carTypeNumber: "0001",
    carTransmission: "CVT",
    carEngineModel: "3ZR-FAE",
    carFuelType: "ガソリン",
    carGrade: "ZS",
    carModelYear: "2018",
    carNumber: "ZRR80-1234567",
    carColorName: "ブラック",
    carColorNo: "070",
    carTrimNo: "FA20",
    carDrive: "2WD",
    carForcedInduction: "ターボ",
    carDoorNm: "5ドア",
    carRemarkNm: "3列目ｼｰﾄ",
    carBodyNm: "HB",
    carSyaType: "80系",
  },
  {
    carMakerName: "本田技研工業",
    carMakerCode: "5",
    carName: "フィット",
    carModel: "5AA-GP5",
    carSharyoKatashiki: "5AA-GP5",
    carDisplacementNm: "",
    carModelNumber: "16703",
    carTypeNumber: "0002",
    carTransmission: "AT",
    carEngineModel: "L15B",
    carFuelType: "ガソリン",
    carGrade: "13G・Fパッケージ",
    carModelYear: "2016",
    carNumber: "GP5-9876543",
    carColorName: "プレミアムスターホワイトパール",
    carColorNo: "NH-731M",
    carTrimNo: "",
    carDrive: "FF",
    carForcedInduction: "",
    carDoorNm: "5ドア",
    carRemarkNm: "",
    carBodyNm: "HB",
    carSyaType: "",
  },
  {
    carMakerName: "スバル",
    carMakerCode: "8",
    carName: "インプレッサ",
    carModel: "DBA-GP7",
    carSharyoKatashiki: "DBA-GP7",
    carDisplacementNm: "2000",
    carModelNumber: "19999",
    carTypeNumber: "0099",
    carTransmission: "MT",
    carEngineModel: "FB20",
    carFuelType: "ガソリン",
    carGrade: "2.0i-S",
    carModelYear: "2020",
    carNumber: "TST-0000001",
    carColorName: "",
    carColorNo: "37J",
    carTrimNo: "TR01",
    carDrive: "4WD",
    carForcedInduction: "ターボ",
    carDoorNm: "4ドア",
    carRemarkNm: "",
    carBodyNm: "",
    carSyaType: "GP系",
  },
];

/**
 * 空の検索条件オブジェクト
 * @returns {Record<string, string>}
 */
export function emptyCriteria() {
  return Object.fromEntries(CRITERIA_FIELDS.map((f) => [f.key, ""]));
}

/**
 * 親画面などから渡された初期値のうち、ダイアログ検索条件として扱う項目だけを取り出す
 * @param {Record<string, string>} [initialCriteria]
 */
export function pickDialogCriteria(initialCriteria = {}) {
  const next = emptyCriteria();
  for (const { key } of CRITERIA_FIELDS) {
    if (initialCriteria[key] == null) continue;
    next[key] = String(initialCriteria[key]);
  }
  return next;
}

/**
 * @param {Record<string, string>} criteria
 * @param {typeof MOCK_ROWS} rows
 */
function mockFilter(criteria, rows) {
  return rows.filter((row) =>
    CRITERIA_FIELDS.every((f) => {
      const need = String(criteria[f.key] || "").trim();
      if (!need) return true;
      if (f.key === "carSharyoKatashiki") {
        const hay = `${row.carSharyoKatashiki || ""} ${row.carModel || ""}`.toLowerCase();
        return hay.includes(need.toLowerCase());
      }
      return String(row[f.key] || "").toLowerCase().includes(need.toLowerCase());
    }),
  );
}

/**
 * モック検索（条件が1つでもあれば絞り込み、すべて空なら全件）
 * @param {Record<string, string>} criteriaState
 * @param {typeof MOCK_ROWS} [rowsSource]
 */
export function runMockSearch(criteriaState, rowsSource = MOCK_ROWS) {
  const hasAny = hasAnyCriteria(criteriaState);
  return hasAny ? mockFilter(criteriaState, rowsSource) : rowsSource;
}

/**
 * 検索条件が1つ以上入力されているか
 * @param {Record<string, string>} criteriaState
 */
export function hasAnyCriteria(criteriaState = {}) {
  return CRITERIA_FIELDS.some((f) => String(criteriaState[f.key] || "").trim());
}

/**
 * 車輌検索ダイアログの状態と操作（既定は p_car プロキシ経由で実データ。`pcarSearchUrl: ""` のときのみモック）
 * @param {Object} options
 * @param {boolean} options.open
 * @param {() => void} options.onClose
 * @param {Object<string, string>} [options.initialCriteria] emptyCriteria と同じキー（省略可）
 * @param {Function} [options.onApply] 行選択時（MOCK_ROWS 相当の行オブジェクトを渡す）
 * @param {number|string} [options.dialogSession]
 * @param {string} [options.pcarSearchUrl] 省略時は `/api/syaryo/p-car` または `NEXT_PUBLIC_SYARYO_PCAR_URL`。空文字 `""` でモックのみ
 * @returns {Object} 画面表示用の値とハンドラ
 */
export function useSyaryoDialog({
  open,
  onClose,
  initialCriteria = {},
  onApply,
  dialogSession = 0,
  pcarSearchUrl,
}) {
  const resolvedPcarSearchUrl = resolveSyaryoPcarSearchUrl(pcarSearchUrl);
  const usePcar = Boolean(resolvedPcarSearchUrl);
  const [criteria, setCriteria] = useState(emptyCriteria);
  const [rows, setRows] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [pcarStatusSummary, setPcarStatusSummary] = useState("");
  /** 直近の API 呼び出しが HTTP 成功か（0件でも true） */
  const [pcarLastFetchOk, setPcarLastFetchOk] = useState(false);
  const [lastPcarRawCount, setLastPcarRawCount] = useState(/** @type {number | null} */ (null));
  const [pcarDebugBundle, setPcarDebugBundle] = useState(/** @type {{ postBody: string; apiRaw: unknown } | null} */ (null));

  const runSearch = useCallback(
    async (crit) => {
      log.debug("START runSearch ------------", crit);
      if (usePcar) {
        setSearchLoading(true);
        setSearchError("");
        setPcarStatusSummary("");
        setPcarLastFetchOk(false);
        setLastPcarRawCount(null);
        setPcarDebugBundle(null);
        try {
          const res = await fetch(resolvedPcarSearchUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ criteria: crit }),
          });
          const json = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(json?.error || `HTTP ${res.status}`);
          }
          const nextRows = Array.isArray(json.rows) ? json.rows : [];
          setRows(nextRows);
          setPcarLastFetchOk(true);
          const rawN = json.rawCount;
          setLastPcarRawCount(typeof rawN === "number" ? rawN : nextRows.length);
          setPcarDebugBundle({
            postBody: typeof json.debugPostBody === "string" ? json.debugPostBody : "",
            apiRaw: json.apiRaw !== undefined ? json.apiRaw : null,
          });
          log.debug("p_car apiRaw", json.apiRaw);
          log.debug("p_car mapped rows sample", nextRows[0]);
          const st = json.status;
          const mode = json.searchMode != null ? String(json.searchMode) : "";
          if (st && typeof st === "object") {
            const parts = [
              mode ? `endpoint=${mode}` : "",
              st.message != null ? String(st.message) : "",
              st.count != null ? `count=${st.count}` : "",
              st.result != null ? `result=${st.result}` : "",
            ].filter(Boolean);
            setPcarStatusSummary(parts.join(" / "));
          } else {
            setPcarStatusSummary(
              [mode ? `endpoint=${mode}` : "", json.rawCount != null ? `件数=${json.rawCount}` : ""].filter(Boolean).join(" / "),
            );
          }
        } catch (e) {
          setSearchError(e?.message || String(e));
          setRows([]);
          setPcarLastFetchOk(false);
          setLastPcarRawCount(null);
          setPcarDebugBundle(null);
        } finally {
          setSearchLoading(false);
        }
        return;
      }
      setRows(runMockSearch(crit));
    },
    [usePcar, resolvedPcarSearchUrl],
  );

  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;

  useEffect(() => {
    if (!open) return;
    log.debug("START useSyaryoDialog syncInitial ------------");
    const next = pickDialogCriteria(initialCriteria);
    setCriteria(next);
    setSearchError("");
    setPcarStatusSummary("");
    setPcarLastFetchOk(false);
    setLastPcarRawCount(null);
    setPcarDebugBundle(null);
    if (hasAnyCriteria(next)) {
      void runSearchRef.current(next);
    } else {
      setRows([]);
    }
  }, [open, dialogSession, initialCriteria]);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSearch = useCallback(async () => {
    log.debug("START handleSearch ------------");
    await runSearch(criteria);
  }, [criteria, runSearch]);

  const handleClearCriteria = useCallback(() => {
    log.debug("START handleClearCriteria ------------");
    setCriteria(emptyCriteria());
  }, []);

  const handleSelectRow = useCallback(
    (row) => {
      log.debug("START handleSelectRow ------------");
      onApply?.(row);
      onClose?.();
    },
    [onApply, onClose],
  );

  return {
    criteria,
    rows,
    criteriaFields: CRITERIA_FIELDS,
    listColumns: CAR_SEARCH_LIST_COLUMNS,
    handleCriteriaChange,
    handleSearch,
    handleClearCriteria,
    handleSelectRow,
    searchLoading,
    searchError,
    pcarStatusSummary,
    usePcar,
    pcarSearchUrlResolved: resolvedPcarSearchUrl,
    pcarLastFetchOk,
    lastPcarRawCount,
    pcarDebugBundle,
  };
}
